import { Avatar, Badge, Button } from '@foundation/ui'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './AppLayout.module.css'
import type { ReactNode } from 'react'
import { api } from '@/lib/api'
import { clearAuthState, getAuthState } from '@/lib/auth-store'

interface AppLayoutProps {
  children: ReactNode
  title?: string
  action?: ReactNode
}

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: '\uD83D\uDCCA' },
  { path: '/testimonials', label: 'Testimonials', icon: '\uD83D\uDCAC', hasBadge: true },
  { path: '/collection-pages', label: 'Collection Pages', icon: '\uD83D\uDCC4' },
  { path: '/widgets', label: 'Widgets', icon: '\uD83E\uDDE9' },
  { path: '/settings', label: 'Settings', icon: '\u2699\uFE0F' },
]

export function AppLayout({ children, title, action }: AppLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = getAuthState()
  const [pendingCount, setPendingCount] = useState(0)

  useEffect(() => {
    api
      .listTestimonials({ status: 'pending', limit: 1 })
      .then((response) => {
        setPendingCount(response.pagination.total)
      })
      .catch(() => {
        // Silently ignore - badge just won't show
      })
  }, [])

  const handleLogout = () => {
    clearAuthState()
    void navigate({ to: '/login' })
  }

  return (
    <div className={styles.layout}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Link to="/" className={styles.logo}>
            <span className={styles.logoIcon}>&#10024;</span>
            <span className={styles.logoText}>ProofPulse</span>
          </Link>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.navItem} ${location.pathname.startsWith(item.path) ? styles.navItemActive : ''}`}
            >
              <span className={styles.navIcon}>{item.icon}</span>
              <span className={styles.navLabel}>{item.label}</span>
              {item.hasBadge && pendingCount > 0 && (
                <Badge variant="warning" className={styles.navBadge}>
                  {pendingCount}
                </Badge>
              )}
            </Link>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.footerVersion}>ProofPulse v1.0</div>
        </div>
      </aside>

      {/* Main content */}
      <div className={styles.main}>
        {/* Top header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            {title && <h1 className={styles.pageTitle}>{title}</h1>}
          </div>
          <div className={styles.headerRight}>
            {action}
            <div className={styles.userMenu}>
              <Avatar name={auth.user?.name ?? auth.user?.email ?? 'U'} size="sm" />
              <span className={styles.userName}>{auth.user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className={styles.content}>{children}</main>
      </div>
    </div>
  )
}
